import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authLogin, authLogout, authMe } from "@/lib/api";

/* AuthContext — admin only.
 *  state.user is:
 *    null      → still checking / no session
 *    object    → logged-in admin
 *    false     → confirmed not authenticated
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const me = await authMe();
      setUser(me);
    } catch (_) {
      setUser(false);
    } finally {
      setBootstrapped(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const data = await authLogin(email, password);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } finally {
      setUser(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, bootstrapped, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
